/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './VehicleStatus.css';

const STATUS_OPTIONS = [
    { value: 'green', label: 'Einsatzbereit', color: '#059669' },
    { value: 'yellow', label: 'Eingeschränkt', color: '#d97706' },
    { value: 'red', label: 'Defekt / Werkstatt', color: '#dc2626' }
];

const TYPE_OPTIONS = [
    'Transporter', 'LKW', 'PKW', 'Anhänger', 'Bagger', 'Radlader', 'Sonstiges'
];

const DAYS_BEFORE_OPTIONS = [
    { value: 30, label: '30 Tage vorher' },
    { value: 14, label: '14 Tage vorher' },
    { value: 7, label: '7 Tage vorher' },
    { value: 3, label: '3 Tage vorher' },
    { value: 1, label: '1 Tag vorher' }
];

const INTERVAL_OPTIONS = [
    { value: 'once', label: 'Einmalig' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'daily', label: 'Täglich' }
];

/**
 * VehicleModal Component
 * 
 * Used for creating and editing vehicle data. Handles comprehensive fields including
 * workshop info, insurance details, and notification configurations.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback triggered when the modal should close
 * @param {Function} props.onSave - Callback triggered with the form payload on save
 * @param {Object} [props.vehicle] - Optional vehicle data for edit mode
 * @param {Array} [props.companyUsers] - List of users for assignment
 * @param {Array} [props.categories] - List of vehicle categories
 * @returns {JSX.Element|null} The rendered modal or null if not open
 */
export default function VehicleModal({
    isOpen,
    onClose,
    onSave,
    vehicle,
    companyUsers = [],
    categories = []

}) {
    if (!isOpen) return null;

    const isEdit = !!vehicle;
    const [showAllBasisData, setShowAllBasisData] = useState(false);
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    const [form, setForm] = useState({
        internal_name: '',
        license_plate: '',
        category_id: '',

        vehicle_class: '',
        make: '',
        model: '',
        year: '',
        vin: '',
        location: '',
        assigned_user_id: '',
        responsible_user_id: '',
        responsible_email: '',
        status: 'green',
        is_ready: true,
        status_reason: '',
        notes: '',
        last_tuv: '',
        next_tuv: '',
        last_inspection: '',
        next_inspection: '',
        last_tech_check: '',
        next_tech_check: '',
        workshop_info: {
            tuv: { name: '', company: '', address: '', phone: '', email: '' },
            uvv: { name: '', company: '', address: '', phone: '', email: '' },
            inspection: { name: '', company: '', address: '', phone: '', email: '' },
            insurance: { company: '', name: '', phone: '', email: '', policy_number: '', address: '' }
        },
        notification_config: {
            days_before: 7,
            interval: 'once',
            has_tuv: true,
            has_inspection: true,
            has_uvv: true,
            custom_deadlines: []
        },
        tuv_email: '',
        inspection_email: '',
        tech_check_email: '',
        location_email: ''
    });

    const [collapsed, setCollapsed] = useState({
        tuv_notif: false, tuv_workshop: false,
        inspection_notif: false, inspection_workshop: false,
        uvv_notif: false, uvv_workshop: false,
        location_notif: false
    });


    useEffect(() => {
        if (isOpen) {
            if (vehicle) {
                const formatDate = (dateStr) => dateStr ? dateStr.split('T')[0] : '';
                setForm({
                    ...vehicle,
                    last_tuv: formatDate(vehicle.last_tuv),
                    next_tuv: formatDate(vehicle.next_tuv),
                    last_inspection: formatDate(vehicle.last_inspection),
                    next_inspection: formatDate(vehicle.next_inspection),
                    next_tech_check: formatDate(vehicle.next_tech_check),
                    assigned_user_id: vehicle.assigned_user_id || '',
                    category_id: vehicle.category_id || '',

                    responsible_user_id: vehicle.responsible_user_id || '',
                    responsible_email: vehicle.responsible_email || '',
                    workshop_info: {
                        tuv: { name: '', company: '', address: '', phone: '', email: '', ...(vehicle.workshop_info?.tuv || {}) },
                        uvv: { name: '', company: '', address: '', phone: '', email: '', ...(vehicle.workshop_info?.uvv || {}) },
                        inspection: { name: '', company: '', address: '', phone: '', email: '', ...(vehicle.workshop_info?.inspection || {}) },
                        insurance: { company: '', name: '', phone: '', email: '', policy_number: '', address: '', ...(vehicle.workshop_info?.insurance || {}) }
                    },
                    notification_config: {
                        days_before: vehicle.notification_config?.days_before || 7,
                        interval: vehicle.notification_config?.interval || 'once',
                        has_tuv: vehicle.notification_config?.has_tuv ?? true,
                        has_inspection: vehicle.notification_config?.has_inspection ?? true,
                        has_uvv: vehicle.notification_config?.has_uvv ?? true,
                        custom_deadlines: vehicle.notification_config?.custom_deadlines || []
                    },
                    tuv_email: vehicle.tuv_email || '',
                    inspection_email: vehicle.inspection_email || '',
                    tech_check_email: vehicle.tech_check_email || '',
                    location_email: vehicle.location_email || ''
                });
            } else {
                setForm({
                    internal_name: '',
                    license_plate: '',
                    category_id: '',

                    vehicle_class: '',
                    make: '',
                    model: '',
                    year: '',
                    vin: '',
                    location: '',
                    assigned_user_id: '',
                    responsible_user_id: '',
                    responsible_email: '',
                    status: 'green',
                    is_ready: true,
                    status_reason: '',
                    notes: '',
                    last_tuv: '',
                    next_tuv: '',
                    last_inspection: '',
                    next_inspection: '',
                    last_tech_check: '',
                    next_tech_check: '',
                    workshop_info: {
                        tuv: { name: '', company: '', address: '', phone: '', email: '' },
                        uvv: { name: '', company: '', address: '', phone: '', email: '' },
                        inspection: { name: '', company: '', address: '', phone: '', email: '' },
                        insurance: { company: '', name: '', phone: '', email: '', policy_number: '', address: '' }
                    },
                    notification_config: {
                        days_before: 7,
                        interval: 'once',
                        has_tuv: true,
                        has_inspection: true,
                        has_uvv: true,
                        custom_deadlines: []
                    },
                    tuv_email: '',
                    inspection_email: '',
                    tech_check_email: '',
                    location_email: ''
                });
            }
            setShowAllBasisData(false);
            setCollapsed({
                tuv_notif: false, tuv_workshop: false,
                inspection_notif: false, inspection_workshop: false,
                uvv_notif: false, uvv_workshop: false,
                location_notif: false
            });
        }
    }, [isOpen, vehicle]);

    const handleChange = (field, value) => {
        setForm(f => {
            const updated = { ...f, [field]: value };
            if (field === 'status') {
                if (value === 'yellow' || value === 'red') {
                    updated.is_ready = false;
                } else if (value === 'green') {
                    updated.is_ready = true;
                }
            }
            return updated;
        });
    };


    const handleWorkshopChange = (type, field, value) => {
        setForm(f => ({
            ...f,
            workshop_info: {
                ...f.workshop_info,
                [type]: {
                    ...f.workshop_info[type],
                    [field]: value
                }
            }
        }));
    };

    const handleNotificationChange = (field, value) => {
        setForm(f => ({
            ...f,
            notification_config: {
                ...f.notification_config,
                [field]: value
            }
        }));
    };

    const addCustomDeadline = () => {
        setForm(f => ({
            ...f,
            notification_config: {
                ...f.notification_config,
                custom_deadlines: [
                    ...(f.notification_config.custom_deadlines || []),
                    { id: Date.now(), name: '', date: '', email: '', workshop_name: '', days_before: 7 }
                ]
            }
        }));
    };

    const updateCustomDeadline = (id, field, value) => {
        setForm(f => ({
            ...f,
            notification_config: {
                ...f.notification_config,
                custom_deadlines: (f.notification_config.custom_deadlines || []).map(cd => 
                    cd.id === id ? { ...cd, [field]: value } : cd
                )
            }
        }));
    };

    const removeCustomDeadline = (id) => {
        setForm(f => ({
            ...f,
            notification_config: {
                ...f.notification_config,
                custom_deadlines: (f.notification_config.custom_deadlines || []).filter(cd => cd.id !== id)
            }
        }));
    };

    const handleSave = () => {
        if (!form.internal_name.trim() || !form.license_plate.trim()) return;
        const payload = { ...form };
        if (payload.assigned_user_id === '') payload.assigned_user_id = null;
        if (payload.category_id === '') payload.category_id = null;
        if (payload.responsible_user_id === '') payload.responsible_user_id = null;
        if (payload.year === '') payload.year = null;
        onSave(payload);
    };

    const toggle = (key) => {
        setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const canSave = form.internal_name.trim() && form.license_plate.trim();

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="modal-content vehicle-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">{isEdit ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug anlegen'}</div>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    <div className="modal-section-title">📦 Basisdaten</div>
                    <div className="modal-row">
                        <div className="modal-group">
                            <label>Interne Bezeichnung <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="text" value={form.internal_name} onChange={e => handleChange('internal_name', e.target.value)} placeholder="z.B. F-001" autoFocus />
                        </div>
                        <div className="modal-group">
                            <label>Kennzeichen <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="text" value={form.license_plate} onChange={e => handleChange('license_plate', e.target.value)} placeholder="B-OS 123" />
                        </div>
                    </div>

                    <div className="modal-row">
                        <div className="modal-group">
                            <label>Fahrzeugtyp</label>
                            <select value={form.category_id} onChange={e => handleChange('category_id', e.target.value)}>
                                <option value="">Kein Typ</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="modal-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '20px' }}>
                             <button type="button" className="btn-action" onClick={() => setShowAllBasisData(!showAllBasisData)}>
                                {showAllBasisData ? '⬆ Weniger Details' : '⬇ Mehr Details anzeigen (Hersteller, VIN...)'}
                             </button>
                        </div>
                    </div>

                    {showAllBasisData && (
                        <div className="basis-details-expanded" style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                            <div className="modal-row">
                                <div className="modal-group">
                                    <label>Baujahr</label>
                                    <input type="number" value={form.year || ''} onChange={e => handleChange('year', e.target.value)} placeholder="z.B. 2022" />
                                </div>
                                <div className="modal-group">
                                    <label>Standort / Parkplatz</label>
                                    <input type="text" value={form.location} onChange={e => handleChange('location', e.target.value)} placeholder="z.B. Betriebshof Nord" />
                                    <div className="sub-toggle" onClick={() => toggle('location_notif')}>
                                        {collapsed.location_notif ? '▲ Benachrichtigung ausblenden' : '▼ Benachrichtigung konfigurieren'}
                                    </div>
                                    {collapsed.location_notif && (
                                        <div className="sub-config-box">
                                            <label>Benachrichtigung an (E-Mail)</label>
                                            <input type="email" value={form.location_email} onChange={e => handleChange('location_email', e.target.value)} placeholder="E-Mail für Standort-Info" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-row">
                                <div className="modal-group">
                                    <label>Hersteller / Modell</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input type="text" value={form.make} onChange={e => handleChange('make', e.target.value)} placeholder="Hersteller" style={{ flex: 1 }} />
                                        <input type="text" value={form.model} onChange={e => handleChange('model', e.target.value)} placeholder="Modell" style={{ flex: 1 }} />
                                    </div>
                                </div>
                                <div className="modal-group">
                                    <label>Fahrgestellnummer (VIN)</label>
                                    <input type="text" value={form.vin} onChange={e => handleChange('vin', e.target.value)} placeholder="WVGZZZ..." />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="modal-section-title">🚦 Status & Zuständigkeit</div>
                    <div className="modal-row">
                        <div className="modal-group">
                            <label>Aktueller Status</label>
                            <div className="status-selector">
                                {STATUS_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`status-btn ${form.status === opt.value ? 'active' : ''}`}
                                        style={{ '--status-color': opt.color }}
                                        onClick={() => handleChange('status', opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="modal-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '25px' }}>
                            <input type="checkbox" id="is_ready" checked={form.is_ready} onChange={e => handleChange('is_ready', e.target.checked)} style={{ width: '20px', height: '20px' }} />
                            <label htmlFor="is_ready" style={{ marginBottom: 0, fontWeight: 700 }}>Einsatzbereit?</label>
                        </div>
                    </div>

                    {form.status !== 'green' && (
                        <div className="modal-group">
                            <label>Grund für Status / Defekt</label>
                            <textarea value={form.status_reason} onChange={e => handleChange('status_reason', e.target.value)} placeholder="Warum ist das Fahrzeug nicht voll einsatzbereit?" rows={1} />
                        </div>
                    )}

                    <div className="modal-row">
                        <div className="modal-group">
                            <label>Zuständiger (Fahrer/Nutzer)</label>
                            <select value={form.assigned_user_id} onChange={e => handleChange('assigned_user_id', e.target.value)}>
                                <option value="">Keinem zugeordnet</option>
                                {companyUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>
                        <div className="modal-group">
                            <label>Verantwortlicher (Verwaltung)</label>
                            <select value={form.responsible_user_id} onChange={e => handleChange('responsible_user_id', e.target.value)}>
                                <option value="">Keiner festgelegt</option>
                                {companyUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="modal-section-title">🛡️ Versicherung & Sonstiges</div>
                    <div className="basis-details-expanded" style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                        <div className="modal-row">
                            <div className="modal-group">
                                <label>Versicherungsgesellschaft / Name</label>
                                <input type="text" value={form.workshop_info.insurance?.company || ''} onChange={e => handleWorkshopChange('insurance', 'company', e.target.value)} placeholder="z.B. Allianz, HUK..." />
                            </div>
                            <div className="modal-group">
                                <label>Versicherungsscheinnummer / Police</label>
                                <input type="text" value={form.workshop_info.insurance?.policy_number || ''} onChange={e => handleWorkshopChange('insurance', 'policy_number', e.target.value)} placeholder="Vers.-Nr." />
                            </div>
                        </div>
                        <div className="modal-row">
                            <div className="modal-group">
                                <label>Ansprechpartner Versicherung</label>
                                <input type="text" value={form.workshop_info.insurance?.name || ''} onChange={e => handleWorkshopChange('insurance', 'name', e.target.value)} placeholder="Name des Maklers / Kontakts" />
                            </div>
                            <div className="modal-group">
                                <label>Telefon</label>
                                <input type="text" value={form.workshop_info.insurance?.phone || ''} onChange={e => handleWorkshopChange('insurance', 'phone', e.target.value)} placeholder="+49 ..." />
                            </div>
                        </div>
                    </div>

                    <div className="modal-section-title">📅 Fristen & Benachrichtigungen</div>
                    
                    {/* TÜV SECTION */}
                    <div className="deadline-category-box">
                        <div className="modal-row" style={{ alignItems: 'flex-start' }}>
                            <div className="modal-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={form.notification_config.has_tuv} 
                                        onChange={e => handleNotificationChange('has_tuv', e.target.checked)} 
                                        style={{ width: '16px', height: '16px', margin: 0 }} 
                                    />
                                    TÜV vorhanden
                                </label>
                                {form.notification_config.has_tuv && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label>Nächster TÜV / Datum</label>
                                        <input type="date" value={form.next_tuv} onChange={e => handleChange('next_tuv', e.target.value)} className="date-input-main" />
                                    </div>
                                )}
                            </div>
                        </div>
                        {form.notification_config.has_tuv && (
                            <div className="sub-config-box">
                                <div className="modal-row" style={{ marginBottom: '10px' }}>
                                    <div className="modal-group">
                                        <label>Benachrichtigung an (E-Mail)</label>
                                        <input type="email" value={form.tuv_email || ''} onChange={e => handleChange('tuv_email', e.target.value)} placeholder="tuev@firma.de" />
                                    </div>
                                    <div className="modal-group">
                                        <label>Erinnerung starten</label>
                                        <select value={form.notification_config.days_before} onChange={e => handleNotificationChange('days_before', parseInt(e.target.value))}>
                                            {DAYS_BEFORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-row" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                    <div className="modal-group">
                                        <label>Werkstatt / Adresse</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={form.workshop_info.tuv?.company || ''} onChange={e => handleWorkshopChange('tuv', 'company', e.target.value)} placeholder="Firma" style={{ flex: 1 }} />
                                            <input type="text" value={form.workshop_info.tuv?.address || ''} onChange={e => handleWorkshopChange('tuv', 'address', e.target.value)} placeholder="Adresse" style={{ flex: 1 }} />
                                        </div>
                                    </div>
                                    <div className="modal-group">
                                        <label>Kontakt (Tel / E-Mail)</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={form.workshop_info.tuv?.phone || ''} onChange={e => handleWorkshopChange('tuv', 'phone', e.target.value)} placeholder="Telefon" style={{ flex: 1 }} />
                                            <input type="email" value={form.workshop_info.tuv?.email || ''} onChange={e => handleWorkshopChange('tuv', 'email', e.target.value)} placeholder="E-Mail" style={{ flex: 1 }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* INSPECTION SECTION */}
                    <div className="deadline-category-box">
                        <div className="modal-row" style={{ alignItems: 'flex-start' }}>
                            <div className="modal-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={form.notification_config.has_inspection} 
                                        onChange={e => handleNotificationChange('has_inspection', e.target.checked)} 
                                        style={{ width: '16px', height: '16px', margin: 0 }} 
                                    />
                                    Inspektion vorhanden
                                </label>
                                {form.notification_config.has_inspection && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label>Nächste Inspektion / Datum</label>
                                        <input type="date" value={form.next_inspection} onChange={e => handleChange('next_inspection', e.target.value)} className="date-input-main" />
                                    </div>
                                )}
                            </div>
                        </div>
                        {form.notification_config.has_inspection && (
                            <div className="sub-config-box">
                                <div className="modal-row" style={{ marginBottom: '10px' }}>
                                    <div className="modal-group">
                                        <label>Benachrichtigung an (E-Mail)</label>
                                        <input type="email" value={form.inspection_email || ''} onChange={e => handleChange('inspection_email', e.target.value)} placeholder="service@firma.de" />
                                    </div>
                                    <div className="modal-group">
                                        <label>Erinnerung starten</label>
                                        <select value={form.notification_config.days_before} onChange={e => handleNotificationChange('days_before', parseInt(e.target.value))}>
                                            {DAYS_BEFORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-row" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                    <div className="modal-group">
                                        <label>Werkstatt / Adresse</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={form.workshop_info.inspection?.company || ''} onChange={e => handleWorkshopChange('inspection', 'company', e.target.value)} placeholder="Firma" style={{ flex: 1 }} />
                                            <input type="text" value={form.workshop_info.inspection?.address || ''} onChange={e => handleWorkshopChange('inspection', 'address', e.target.value)} placeholder="Adresse" style={{ flex: 1 }} />
                                        </div>
                                    </div>
                                    <div className="modal-group">
                                        <label>Kontakt (Tel / E-Mail)</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={form.workshop_info.inspection?.phone || ''} onChange={e => handleWorkshopChange('inspection', 'phone', e.target.value)} placeholder="Telefon" style={{ flex: 1 }} />
                                            <input type="email" value={form.workshop_info.inspection?.email || ''} onChange={e => handleWorkshopChange('inspection', 'email', e.target.value)} placeholder="E-Mail" style={{ flex: 1 }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* UVV SECTION */}
                    <div className="deadline-category-box">
                        <div className="modal-row" style={{ alignItems: 'flex-start' }}>
                            <div className="modal-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={form.notification_config.has_uvv} 
                                        onChange={e => handleNotificationChange('has_uvv', e.target.checked)} 
                                        style={{ width: '16px', height: '16px', margin: 0 }} 
                                    />
                                    UVV / Prüfung vorhanden
                                </label>
                                {form.notification_config.has_uvv && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label>Nächste UVV / Datum</label>
                                        <input type="date" value={form.next_tech_check} onChange={e => handleChange('next_tech_check', e.target.value)} className="date-input-main" />
                                    </div>
                                )}
                            </div>
                            {form.notification_config.has_uvv && (
                                <div className="deadline-actions">
                                    <button type="button" className={`toggle-btn ${collapsed.uvv_notif ? 'active' : ''}`} onClick={() => toggle('uvv_notif')}>
                                        {collapsed.uvv_notif ? '🔔 Benachrichtigung (Aktiv)' : '🔔 Benachrichtigung'}
                                    </button>
                                    <button type="button" className={`toggle-btn ${collapsed.uvv_workshop ? 'active' : ''}`} onClick={() => toggle('uvv_workshop')}>
                                        {collapsed.uvv_workshop ? '🏥 Werkstatt (Aktiv)' : '🏥 Werkstatt'}
                                    </button>
                                </div>
                            )}
                        </div>
                        {form.notification_config.has_uvv && collapsed.uvv_notif && (
                            <div className="sub-config-box">
                                <div className="modal-row">
                                    <div className="modal-group">
                                        <label>Benachrichtigung an (E-Mail)</label>
                                        <input type="email" value={form.tech_check_email} onChange={e => handleChange('tech_check_email', e.target.value)} placeholder="uvv@firma.de" />
                                    </div>
                                    <div className="modal-group">
                                        <label>Erinnerung starten</label>
                                        <select value={form.notification_config.days_before} onChange={e => handleNotificationChange('days_before', parseInt(e.target.value))}>
                                            {DAYS_BEFORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        {form.notification_config.has_uvv && collapsed.uvv_workshop && (
                            <div className="sub-config-box workshop">
                                <div className="modal-row">
                                    <div className="modal-group"><label>Prüfstelle</label><input type="text" value={form.workshop_info.uvv?.company || ''} onChange={e => handleWorkshopChange('uvv', 'company', e.target.value)} /></div>
                                    <div className="modal-group"><label>Ansprechpartner</label><input type="text" value={form.workshop_info.uvv?.name || ''} onChange={e => handleWorkshopChange('uvv', 'name', e.target.value)} /></div>
                                </div>
                                <div className="modal-row">
                                    <div className="modal-group"><label>Telefon</label><input type="text" value={form.workshop_info.uvv?.phone || ''} onChange={e => handleWorkshopChange('uvv', 'phone', e.target.value)} /></div>
                                    <div className="modal-group"><label>E-Mail</label><input type="email" value={form.workshop_info.uvv?.email || ''} onChange={e => handleWorkshopChange('uvv', 'email', e.target.value)} /></div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* CUSTOM DEADLINES SECTION */}
                    {(form.notification_config.custom_deadlines || []).map((cd) => (
                        <div key={cd.id} className="deadline-category-box" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                            <div className="modal-row" style={{ alignItems: 'flex-start' }}>
                                <div className="modal-group" style={{ flex: 1.5 }}>
                                    <label>Individuelle Frist / Bezeichnung</label>
                                    <input 
                                        type="text" 
                                        value={cd.name} 
                                        onChange={e => updateCustomDeadline(cd.id, 'name', e.target.value)} 
                                        placeholder="z.B. Wartung Kühlung" 
                                        style={{ border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div className="modal-group" style={{ flex: 1 }}>
                                    <label>Datum</label>
                                    <input 
                                        type="date" 
                                        value={cd.date} 
                                        onChange={e => updateCustomDeadline(cd.id, 'date', e.target.value)} 
                                        className="date-input-main" 
                                    />
                                </div>
                            </div>
                            <div className="sub-config-box" style={{ marginTop: '10px' }}>
                                <div className="modal-row">
                                    <div className="modal-group">
                                        <label>Benachrichtigung an (E-Mail)</label>
                                        <input 
                                            type="email" 
                                            value={cd.email} 
                                            onChange={e => updateCustomDeadline(cd.id, 'email', e.target.value)} 
                                            placeholder="E-Mail für diese Frist" 
                                        />
                                    </div>
                                    <div className="modal-group">
                                        <label>Erinnerung starten</label>
                                        <select 
                                            value={cd.days_before} 
                                            onChange={e => updateCustomDeadline(cd.id, 'days_before', parseInt(e.target.value))}
                                        >
                                            {DAYS_BEFORE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-row" style={{ marginTop: '10px' }}>
                                     <div className="modal-group">
                                        <label>Werkstatt / Partner</label>
                                        <input 
                                            type="text" 
                                            value={cd.workshop_name} 
                                            onChange={e => updateCustomDeadline(cd.id, 'workshop_name', e.target.value)} 
                                            placeholder="Zuständiger Partner" 
                                        />
                                    </div>
                                    <div className="modal-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <button type="button" onClick={() => removeCustomDeadline(cd.id)} className="btn-modal-cancel" style={{ width: '100%', padding: '10px', color: '#ef4444' }}>
                                            🗑️ Frist löschen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div style={{ padding: '0 20px 20px 20px', textAlign: 'center' }}>
                        <button type="button" onClick={addCustomDeadline} className="btn-secondary" style={{ width: '100%', padding: '12px', borderStyle: 'dashed', background: 'transparent' }}>
                            + Weitere Frist hinzufügen
                        </button>
                    </div>

                    <div className="modal-group" style={{ marginTop: '20px', padding: '0 20px' }}>
                        <label>Interne Notizen</label>
                        <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Weitere Infos zum Fahrzeug..." rows={2} />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose}>Abbrechen</button>
                    <button className="btn-modal-save" onClick={handleSave} disabled={!canSave}>
                        {isEdit ? 'Änderungen speichern' : 'Fahrzeug anlegen'}
                    </button>
                </div>
            </div>
        </div>
    );
}
